// components/pages/ContactPage.tsx
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Aquí iría la lógica para enviar el mensaje
    console.log('Formulario enviado:', formData);
    setShowSuccess(true);
    setFormData({ name: '', email: '', message: '' });
    
    // Ocultar el mensaje después de 5 segundos
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const contactInfo = [
    {
      icon: <PhoneIcon />,
      title: 'Teléfono',
      info: '+56 9 1234 5678',
      description: 'Llámanos para pedidos telefónicos'
    },
    {
      icon: <EmailIcon />,
      title: 'Email',
      info: 'contacto@elpinon.cl',
      description: 'Escríbenos tus consultas'
    },
    {
      icon: <LocationIcon />,
      title: 'Dirección',
      info: 'Av. Principal 123, Comuna',
      description: 'Visítanos en nuestra tienda física'
    },
    {
      icon: <TimeIcon />,
      title: 'Horarios',
      info: 'Lun - Dom: 8:00 - 22:00',
      description: 'Estamos abiertos todos los días'
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom>
          Contacto
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          ¿Tienes alguna pregunta? ¡Estamos aquí para ayudarte!
        </Typography>
      </Box>

      {showSuccess && (
        <Alert severity="success" sx={{ mb: 4 }}>
          ¡Gracias por tu mensaje! Te responderemos pronto.
        </Alert>
      )}

      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4 
        }}
      >
        {/* Información de contacto */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            Información de Contacto
          </Typography>
          
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3,
              mb: 4
            }}
          >
            {contactInfo.map((item, index) => (
              <Card key={index} sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Box color="primary.main" mr={1}>
                      {item.icon}
                    </Box>
                    <Typography variant="h6">
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {item.info}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Síguenos en redes sociales
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mantente al día con nuestras ofertas y novedades en nuestras redes sociales.
            </Typography>
          </Box>
        </Box>

        {/* Formulario de contacto */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Envíanos un mensaje
              </Typography>
              
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Nombre completo"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label="Correo electrónico"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label="Mensaje"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  Enviar Mensaje
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Mapa o información adicional */}
      <Box mt={6} textAlign="center" sx={{ py: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom>
          ¿Necesitas ayuda inmediata?
        </Typography>
        <Typography variant="body1" paragraph>
          Para pedidos urgentes o consultas inmediatas, puedes llamarnos directamente 
          o visitarnos en nuestra tienda física.
        </Typography>
        <Button variant="contained" size="large" startIcon={<PhoneIcon />}>
          Llamar Ahora
        </Button>
      </Box>
    </Container>
  );
};

export default ContactPage;